import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";

// Components
import Layout from "@/components/Layout";
import GuidedTour from "@/components/GuidedTour";

// Pages
import LoginPage from "@/pages/LoginPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import AziendePage from "@/pages/AziendePage";
import ElogiPage from "@/pages/ElogiPage";
import ElogioDetailPage from "@/pages/ElogioDetailPage";
import QRCodePage from "@/pages/QRCodePage";
import AgentiPage from "@/pages/AgentiPage";
import AgenteElogiPage from "@/pages/AgenteElogiPage";
import OrdiniPage from "@/pages/OrdiniPage";
import ImpostazioniPage from "@/pages/ImpostazioniPage";
import CommissioniPage from "@/pages/CommissioniPage";
import UtentiPage from "@/pages/UtentiPage";
import ProfiloPage from "@/pages/ProfiloPage";
import PagamentoCompletatoPage from "@/pages/PagamentoCompletatoPage";

// Protected Route
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.ruolo)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Protected - All roles */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/elogi" element={<ProtectedRoute><ElogiPage /></ProtectedRoute>} />
      <Route path="/elogi/:id" element={<ProtectedRoute><ElogioDetailPage /></ProtectedRoute>} />
      <Route path="/qrcode" element={<ProtectedRoute><QRCodePage /></ProtectedRoute>} />
      <Route path="/pagamento-completato" element={<ProtectedRoute><PagamentoCompletatoPage /></ProtectedRoute>} />

      {/* Protected - Azienda only */}
      <Route 
        path="/profilo" 
        element={<ProtectedRoute allowedRoles={['azienda']}><ProfiloPage /></ProtectedRoute>} 
      />

      {/* Protected - Admin & Agente */}
      <Route 
        path="/aziende" 
        element={<ProtectedRoute allowedRoles={['admin', 'agente']}><AziendePage /></ProtectedRoute>} 
      />
      <Route 
        path="/commissioni" 
        element={<ProtectedRoute allowedRoles={['agente']}><CommissioniPage /></ProtectedRoute>} 
      />

      {/* Protected - Admin only */}
      <Route 
        path="/agenti" 
        element={<ProtectedRoute allowedRoles={['admin']}><AgentiPage /></ProtectedRoute>} 
      />
      <Route 
        path="/agenti/:idAgente/elogi" 
        element={<ProtectedRoute allowedRoles={['admin']}><AgenteElogiPage /></ProtectedRoute>} 
      />
      <Route 
        path="/utenti" 
        element={<ProtectedRoute allowedRoles={['admin']}><UtentiPage /></ProtectedRoute>} 
      />
      <Route 
        path="/ordini" 
        element={<ProtectedRoute allowedRoles={['admin', 'azienda']}><OrdiniPage /></ProtectedRoute>} 
      />
      <Route 
        path="/impostazioni" 
        element={<ProtectedRoute allowedRoles={['admin']}><ImpostazioniPage /></ProtectedRoute>} 
      />

      {/* Redirects */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <GuidedTour />
        <Toaster 
          position="top-right" 
          richColors 
          closeButton
          toastOptions={{
            style: {
              fontFamily: 'Inter, sans-serif',
            },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
